import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lead } from '@/types/lead';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Trash2, Plus, FileText, Search, Phone, Mail, Globe, MoreVertical, StickyNote, Link, Info } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import WebSearchPanel from './WebSearchPanel';
import { useToast } from '@/hooks/use-toast';
import { enrichLeadWithWebSearch } from '@/services/webSearchService';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { formatEmailForDisplay, getPrimaryEmail } from '@/utils/emailUtils';
import { cn } from '@/lib/utils';

interface CardViewProps {
  leads: Lead[];
  onToggleFavorite: (id: string) => void;
  onDelete: (id: string) => void;
  onAddNote: (leadId: string, content: string) => void;
  onAddDocument: (leadId: string, name: string, url: string, type: string) => void;
}

const CardView: React.FC<CardViewProps> = ({
  leads,
  onToggleFavorite,
  onDelete,
  onAddNote,
  onAddDocument,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [webSearchOpen, setWebSearchOpen] = useState<Record<string, boolean>>({});
  const [enriching, setEnriching] = useState<Record<string, boolean>>({});
  const [noteDialogOpen, setNoteDialogOpen] = useState<Record<string, boolean>>({});
  const [documentDialogOpen, setDocumentDialogOpen] = useState<Record<string, boolean>>({});

  const handleEnrichWithWebSearch = async (lead: Lead) => {
    setEnriching(prev => ({ ...prev, [lead.id]: true }));
    try {
      const result = await enrichLeadWithWebSearch(lead);
      
      if (result) {
        toast({
          title: "Web search completed",
          description: `Successfully retrieved web information for ${lead.name}`,
        });
        
        setWebSearchOpen(prev => ({ ...prev, [lead.id]: true }));
      } else {
        toast({
          variant: "destructive",
          title: "Web search failed",
          description: "Unable to retrieve web information. Please try again later.",
        });
      }
    } catch (error) {
      console.error('Error enriching lead with web search:', error);
      toast({
        variant: "destructive",
        title: "Web search failed",
        description: "An error occurred while retrieving web information.",
      });
    } finally {
      setEnriching(prev => ({ ...prev, [lead.id]: false }));
    }
  };

  const handleCardClick = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 animate-fade-in">
      {leads.map((lead) => (
        <Card key={lead.id} className="elevation-lifted hover:elevation-floating border border-border/40 rounded-xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
          <div 
            className="cursor-pointer flex-grow flex flex-col"
            onClick={() => handleCardClick(lead.id)}
          >
            <CardHeader className="p-4 sm:p-5 pb-3 bg-gradient-to-br from-muted/30 to-muted/10 border-b border-border/40 flex-row justify-between items-start space-y-0 flex-shrink-0">
              <div className="overflow-hidden flex-1 min-w-0">
                <h3 className="font-semibold text-xl tracking-tight truncate mb-1">{lead.name}</h3>
                <p className="text-sm text-muted-foreground/80 truncate">{lead.address}</p>
              </div>
              
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(lead.id);
                  }}
                  className={cn(
                    "transition-all hover:scale-110 h-9 w-9 touch-manipulation",
                    lead.isFavorite ? "text-yellow-500" : "text-muted-foreground"
                  )}
                >
                  <Star className={lead.isFavorite ? "h-5 w-5 fill-yellow-500" : "h-5 w-5"} />
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-9 w-9 touch-manipulation">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setNoteDialogOpen(prev => ({ ...prev, [lead.id]: true }))}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Note
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDocumentDialogOpen(prev => ({ ...prev, [lead.id]: true }))}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Document
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setWebSearchOpen(prev => ({ ...prev, [lead.id]: true }))}>
                      <Search className="h-4 w-4 mr-2" />
                      Business Details
                    </DropdownMenuItem>
                    {lead.createdBy === user?.id && (
                      <DropdownMenuItem 
                        className="text-destructive focus:text-destructive" 
                        onClick={() => onDelete(lead.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            
            <CardContent className="p-4 sm:p-5 pt-4 flex-grow overflow-hidden space-y-4">
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gradient-to-br from-primary/10 via-muted to-primary/5 border border-border/30">
                {lead.imageUrl ? (
                  <>
                    <img
                      src={lead.imageUrl}
                      alt={lead.name}
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-muted-foreground/50">
                      <FileText className="h-12 w-12 mx-auto mb-2" />
                      <p className="text-xs">No image</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <div className="flex flex-wrap gap-2.5 mb-4">
                  {lead.category && (
                    <Badge 
                      variant="outline" 
                      className="text-xs font-medium border-primary/30 bg-gradient-to-r from-primary/15 to-primary/8 px-3 py-1 hover:from-primary/20 hover:to-primary/10 transition-colors"
                    >
                      <span className="mr-1.5">📍</span>
                      {lead.category}
                    </Badge>
                  )}
                  
                  {lead.rating && (
                    <Badge 
                      variant="secondary" 
                      className="text-xs font-medium bg-gradient-to-r from-yellow-500/15 to-orange-500/15 border border-yellow-500/30 px-3 py-1"
                    >
                      <Star className="h-3.5 w-3.5 mr-1.5 fill-yellow-500 text-yellow-500" />
                      <span className="font-semibold">{lead.rating}</span>
                      <span className="text-muted-foreground ml-1">({lead.reviewCount})</span>
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  {lead.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Phone className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <a 
                        href={`tel:${lead.phone}`} 
                        className="hover:text-primary transition-colors truncate hover:underline underline-offset-2 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.phone}
                      </a>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Mail className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <a 
                        href={`mailto:${getPrimaryEmail(lead.email)}`} 
                        className="hover:text-primary transition-colors truncate hover:underline underline-offset-2 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatEmailForDisplay(lead.email)}
                      </a>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Globe className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <a 
                        href={lead.website} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:text-primary transition-colors truncate hover:underline underline-offset-2 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.website?.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
                
                {(lead.notes.length > 0 || lead.documents.length > 0) && (
                  <div className="border-t border-border/40 pt-4 mt-4 space-y-4">
                    {lead.notes.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2.5">
                          <StickyNote className="h-3.5 w-3.5 text-primary/70" />
                          Notes ({lead.notes.length})
                        </div>
                        <ul className="text-sm space-y-2">
                          {lead.notes.slice(0, 1).map((note) => (
                            <li key={note.id} className="p-3 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/10 text-muted-foreground">
                              <p className="line-clamp-2 text-sm">{note.content}</p>
                              <p className="text-xs text-muted-foreground/70 mt-1.5">
                                {format(new Date(note.createdAt), 'MMM d, yyyy')}
                              </p>
                            </li>
                          ))}
                          {lead.notes.length > 1 && (
                            <p className="text-xs text-primary font-medium">
                              +{lead.notes.length - 1} more {lead.notes.length === 2 ? 'note' : 'notes'}
                            </p>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {lead.documents.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2.5">
                          <FileText className="h-3.5 w-3.5 text-primary/70" />
                          Documents ({lead.documents.length})
                        </div>
                        <ul className="text-sm space-y-1.5">
                          {lead.documents.slice(0, 2).map((doc) => (
                            <li key={doc.id}>
                              <a 
                                href={doc.url} 
                                className="flex items-center gap-2 text-primary hover:text-primary/80 text-xs truncate p-2 rounded-md hover:bg-primary/5 border border-transparent hover:border-primary/20 transition-all group/doc" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FileText className="h-3.5 w-3.5 flex-shrink-0 group-hover/doc:scale-110 transition-transform" />
                                <span className="truncate">{doc.name}</span>
                              </a>
                            </li>
                          ))}
                          {lead.documents.length > 2 && (
                            <p className="text-xs text-primary font-medium pl-2 pt-1">
                              +{lead.documents.length - 2} more
                            </p>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </div>

          <Dialog
            open={noteDialogOpen[lead.id] || false} 
            onOpenChange={(open) => setNoteDialogOpen(prev => ({ ...prev, [lead.id]: open }))}
          >
            <DialogContent className="sm:max-w-[480px] p-0 elevation-floating backdrop-blur-md">
              <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b border-border/40">
                <div className="flex items-center gap-3">
                  <StickyNote className="h-5 w-5 text-primary" />
                  <DialogTitle className="text-2xl font-semibold tracking-tight">Add Note</DialogTitle>
                </div>
                <DialogDescription className="text-muted-foreground/70">Keep important information about this lead</DialogDescription>
              </DialogHeader>
              <AddNoteForm 
                leadId={lead.id} 
                onAddNote={(leadId, content) => {
                  onAddNote(leadId, content);
                  setNoteDialogOpen(prev => ({ ...prev, [lead.id]: false }));
                }} 
              />
            </DialogContent>
          </Dialog>

          <Dialog 
            open={documentDialogOpen[lead.id] || false} 
            onOpenChange={(open) => setDocumentDialogOpen(prev => ({ ...prev, [lead.id]: open }))}
          >
            <DialogContent className="sm:max-w-[480px] p-0 elevation-floating backdrop-blur-md">
              <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b border-border/40">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <DialogTitle className="text-2xl font-semibold tracking-tight">Add Document</DialogTitle>
                </div>
                <DialogDescription className="text-muted-foreground/70">Attach important files or links to this lead</DialogDescription>
              </DialogHeader>
              <AddDocumentForm 
                leadId={lead.id} 
                onAddDocument={(leadId, name, url, type) => {
                  onAddDocument(leadId, name, url, type);
                  setDocumentDialogOpen(prev => ({ ...prev, [lead.id]: false }));
                }} 
              />
            </DialogContent>
          </Dialog>

          <Dialog 
            open={webSearchOpen[lead.id] || false} 
            onOpenChange={(open) => setWebSearchOpen(prev => ({ ...prev, [lead.id]: open }))}
          >
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Web Information for {lead.name}</DialogTitle>
                <DialogDescription>
                  Detailed information gathered from across the web
                </DialogDescription>
              </DialogHeader>
              <WebSearchPanel 
                lead={lead} 
                onDataUpdated={() => {
                  // You might want to refresh lead data here
                }} 
              />
            </DialogContent>
          </Dialog>
        </Card>
      ))}
    </div>
  );
};

interface AddNoteFormProps {
  leadId: string;
  onAddNote: (leadId: string, content: string) => void;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ leadId, onAddNote }) => {
  const [note, setNote] = React.useState('');
  const maxLength = 500;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (note.trim()) {
      onAddNote(leadId, note);
      setNote('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      <div className="p-6 pt-5 space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground/90">Note Content</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What would you like to remember about this lead?"
            className="min-h-[140px] p-4 transition-all duration-200"
            maxLength={maxLength}
          />
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground">⌘+Enter to save</p>
            <span className="text-xs text-muted-foreground">{note.length}/{maxLength}</span>
          </div>
        </div>
      </div>
      <div className="border-t border-border/40 px-6 py-4 bg-muted/20 flex justify-end gap-3">
        <Button type="submit" variant="gradient" disabled={!note.trim()}>
          Save Note
        </Button>
      </div>
    </form>
  );
};

interface AddDocumentFormProps {
  leadId: string;
  onAddDocument: (leadId: string, name: string, url: string, type: string) => void;
}

const AddDocumentForm: React.FC<AddDocumentFormProps> = ({ leadId, onAddDocument }) => {
  const [name, setName] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [urlError, setUrlError] = React.useState('');

  const validateUrl = (urlString: string) => {
    if (!urlString) {
      setUrlError('');
      return false;
    }
    try {
      new URL(urlString);
      setUrlError('');
      return true;
    } catch {
      setUrlError('Please enter a valid URL');
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && url.trim() && validateUrl(url)) {
      // Simple file type inference
      const fileExtension = url.split('.').pop()?.toLowerCase() || '';
      const type = fileExtension === 'pdf' ? 'pdf' : 
                  ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension) ? 'image' : 
                  'document';
      
      onAddDocument(leadId, name, url, type);
      setName('');
      setUrl('');
      setUrlError('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && name.trim() && url.trim()) {
      handleSubmit(e);
    }
  };

  const isValid = name.trim() && url.trim() && !urlError;

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      <div className="p-6 pt-5 space-y-6">
        <div className="space-y-2">
          <label htmlFor="docName" className="flex items-center gap-2 text-sm font-medium text-foreground/90">
            <FileText className="h-4 w-4" />
            Document Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="docName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Contract, Proposal, Quote"
            className="transition-all duration-200"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="docUrl" className="flex items-center gap-2 text-sm font-medium text-foreground/90">
            <Link className="h-4 w-4" />
            Document URL <span className="text-destructive">*</span>
          </label>
          <Input
            id="docUrl"
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              validateUrl(e.target.value);
            }}
            placeholder="https://example.com/document.pdf"
            className={`transition-all duration-200 ${urlError ? 'border-destructive/50 focus-visible:ring-destructive/20' : ''}`}
            hasError={!!urlError}
          />
          {urlError ? (
            <p className="text-xs text-destructive font-medium flex items-center gap-1">
              {urlError}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              Link to PDF, Google Doc, or any online file
            </p>
          )}
        </div>
      </div>
      <div className="border-t border-border/40 px-6 py-4 bg-muted/20 flex justify-between items-center">
        <span className="text-xs text-muted-foreground">⌘+Enter to save</span>
        <Button type="submit" variant="gradient" disabled={!isValid}>
          Add Document
        </Button>
      </div>
    </form>
  );
};

export default CardView;
